/** The two ways HTML is tidied on its way out of the editor.
 *
 *  What is pasted is held to a short list, because what Outlook, Word and
 *  Google Docs put on the clipboard is mostly styling: a paragraph from Word
 *  arrives as a dozen nested spans and conditional comments. What is saved is
 *  left alone apart from what the editor itself got wrong, because a body
 *  opened to fix a typo may be a migrated one with tables and mention markup
 *  in it, and those have to survive the edit. The server sanitises both
 *  against its allow-list regardless, and is the side that counts. */

/** Elements a paste keeps, with what they become. */
const pasteKeeps: Record<string, string> = {
  P: 'p', BR: 'br', STRONG: 'strong', B: 'strong', EM: 'em', I: 'em',
  A: 'a', UL: 'ul', OL: 'ol', LI: 'li',
  H1: 'h2', H2: 'h2', H3: 'h3', H4: 'h4', H5: 'h4', H6: 'h4',
  BLOCKQUOTE: 'blockquote', PRE: 'pre', CODE: 'code',
  TABLE: 'table', THEAD: 'thead', TBODY: 'tbody', TR: 'tr', TH: 'th', TD: 'td',
  HR: 'hr', DEL: 'del', S: 'del', STRIKE: 'del', IMG: 'img',
};

/** Elements a paste drops together with everything inside them. */
const pasteDrops = new Set([
  'SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'HEAD', 'NOSCRIPT', 'TEMPLATE',
  'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'CANVAS', 'VIDEO', 'AUDIO',
  'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'FORM',
]);

const blocks = new Set([
  'P', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'HR', 'DIV', 'FIGURE', 'DL', 'SECTION', 'ARTICLE',
]);

/** Our own images, the only kind a paste keeps: one pointing at another site
 *  is refused by the page's policy anyway, and copying it across would mostly
 *  be refused by that site. */
const ownImage = /^\/assets\/inline\/[^"<>\s]+$/;

/** What was on the clipboard, held to the short list.
 *
 *  `ours` says whether an image still showing its local copy was put in by
 *  this editor: cut and pasted, or dragged, to move it before the body is
 *  saved. Such an image keeps its number, so tidy() can still give it its
 *  real address; one from anywhere else is dropped like any foreign image. */
export function cleanPasted(html: string, ours: (upload: string) => boolean = () => false): string {
  const source = new DOMParser().parseFromString(html, 'text/html');
  const out = document.createElement('template');
  for (const node of source.body.childNodes) copy(node, out.content, ours);
  dropEmptyParagraphs(out.content);
  return out.innerHTML;
}

function copy(node: Node, into: Node, ours: (upload: string) => boolean): void {
  if (node.nodeType === Node.TEXT_NODE) {
    into.appendChild(document.createTextNode(node.textContent ?? ''));
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return; // comments, Word's <!--[if …]>
  const el = node as HTMLElement;
  const tag = el.tagName.toUpperCase();
  if (pasteDrops.has(tag)) return;

  const children = (target: Node) => { for (const child of el.childNodes) copy(child, target, ours); };
  const style = el.getAttribute('style') ?? '';

  // Google Docs wraps the whole document in <b style="font-weight:normal">.
  if (tag === 'B' && /font-weight:\s*(normal|400)/i.test(style)) { children(into); return; }

  let name = pasteKeeps[tag];

  // Docs and Outlook mark bold and italic with styles on spans.
  if (!name && tag === 'SPAN') {
    let target = into;
    if (/font-weight:\s*(bold|[6-9]00)/i.test(style)) target = target.appendChild(document.createElement('strong'));
    if (/font-style:\s*italic/i.test(style)) target = target.appendChild(document.createElement('em'));
    children(target);
    return;
  }

  // A div of text is a paragraph; a div of paragraphs is only a wrapper.
  if (!name && tag === 'DIV') {
    if ([...el.children].some((c) => blocks.has(c.tagName.toUpperCase()))) children(into);
    else children(into.appendChild(document.createElement('p')));
    return;
  }

  if (!name) { children(into); return; }

  if (name === 'img') {
    const src = el.getAttribute('src') ?? '';
    const upload = el.getAttribute('data-upload');
    const img = document.createElement('img');
    if (upload !== null && ours(upload) && /^data:image\//i.test(src)) {
      img.setAttribute('src', src);
      img.setAttribute('data-upload', upload);
    } else {
      const path = src.startsWith(location.origin) ? src.slice(location.origin.length) : src;
      if (!ownImage.test(path)) return;
      img.setAttribute('src', path);
    }
    const alt = el.getAttribute('alt');
    if (alt) img.setAttribute('alt', alt);
    // A size set in the editor, which travels with an image moved by cut and
    // paste. Pixel sizes from elsewhere do not: they were for another page.
    const width = el.getAttribute('width') ?? '';
    if (/^\d{1,3}%$/.test(width)) img.setAttribute('width', width);
    into.appendChild(img);
    return;
  }

  // Attributes are never copied across, bar the link's address.
  const made = document.createElement(name);
  if (name === 'a') {
    const href = el.getAttribute('href') ?? '';
    if (/^(https?:|mailto:|\/)/i.test(href)) made.setAttribute('href', href);
    else { children(into); return; }
  }
  children(made);
  into.appendChild(made);
}

/** Word ends nearly every paragraph with an empty one, as <p>&nbsp;</p>. */
function dropEmptyParagraphs(root: ParentNode): void {
  for (const p of root.querySelectorAll('p')) {
    if (!p.querySelector('img, br') && !p.textContent?.replace(/ /g, ' ').trim()) p.remove();
  }
}

/** Plain text as paragraphs: blank lines between them, breaks within. */
export function textToHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeText(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** What the editor holds, as the body to send, or '' when nothing was written.
 *
 *  `uploaded` maps each image the editor put in to the address it ended up
 *  at. On screen the image keeps showing the local copy, on a data: address;
 *  the map is what turns it into the real one, and it does that for one that
 *  undo and redo brought back too. An image still uploading, or that never
 *  made it, is left out. */
export function tidy(editor: Node, uploaded: Map<string, string>): string {
  // A copy of the nodes, not a reparse of their HTML: the parser closes a <p>
  // the moment a list opens inside it, and a reparse would leave an empty
  // paragraph behind before every list instead of the list on its own.
  const t = document.createElement('template');
  t.content.append(...editor.cloneNode(true).childNodes);
  const root = t.content;

  for (const img of root.querySelectorAll('img')) {
    const id = img.getAttribute('data-upload');
    if (id !== null) {
      const src = uploaded.get(id);
      if (!src) { img.remove(); continue; }
      img.setAttribute('src', src);
      img.removeAttribute('data-upload');
    }
    if (/^(data|blob):/i.test(img.getAttribute('src') ?? '')) img.remove();
  }

  // Chrome writes the computed style of whatever it touched into the markup.
  for (const el of root.querySelectorAll('[style]')) el.removeAttribute('style');

  // Chrome will put a list inside a paragraph, which no parser keeps as it
  // is; the paragraph goes and what it held stays.
  for (let p = root.querySelector('p:has(> :is(ul, ol, p, h1, h2, h3, h4, h5, h6, blockquote, pre, table, div, hr))');
    p; p = root.querySelector('p:has(> :is(ul, ol, p, h1, h2, h3, h4, h5, h6, blockquote, pre, table, div, hr))')) {
    p.replaceWith(...p.childNodes);
  }

  // A div of text is a paragraph; a div of blocks is only a wrapper, and a
  // paragraph around a list or a table would not survive the next parse.
  const firstDiv = () => [...root.children].find((el) => el.tagName === 'DIV');
  for (let div = firstDiv(); div; div = firstDiv()) {
    if ([...div.children].some((c) => blocks.has(c.tagName.toUpperCase()))) {
      div.replaceWith(...div.childNodes);
    } else {
      const p = document.createElement('p');
      p.append(...div.childNodes);
      div.replaceWith(p);
    }
  }

  // Text typed into an empty box, or left beside a block, sits loose at the
  // top level; a body is made of blocks.
  let run: HTMLParagraphElement | null = null;
  // A copy: childNodes is live, and the loop moves nodes out of it.
  // oxlint-disable-next-line unicorn/no-useless-spread
  for (const node of [...root.childNodes]) {
    const isBlock = node.nodeType === Node.ELEMENT_NODE && blocks.has((node as Element).tagName.toUpperCase());
    if (isBlock) { run = null; continue; }
    if (!run) {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) { node.remove(); continue; }
      run = document.createElement('p');
      root.insertBefore(run, node);
    }
    run.appendChild(node);
  }

  // The empty paragraph a box starts with, and the ones Enter leaves at the end.
  const empty = (el: Element | null) =>
    !!el && el.tagName === 'P' && !el.querySelector('img') && !el.textContent?.replace(/ /g, ' ').trim();
  while (empty(root.firstElementChild)) root.firstElementChild!.remove();
  while (empty(root.lastElementChild)) root.lastElementChild!.remove();

  if (!root.textContent?.trim() && !root.querySelector('img')) return '';
  return t.innerHTML;
}
